import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import Stack from "veneer/Stack";

import {
  Application,
  Schema,
} from "cortex/ui/src/fleet/view/application/model";
import * as ApplicationSelectors from "cortex/ui/src/redux-store/applications";
import { Header } from "cortex/ui/src/ui/header";
import { getAppParamKey } from "cortex/ui/src/util/application";
import { sortByDisplayNameAsc } from "cortex/ui/src/util/sortByProp";

import { OptionalParamsSelector } from "./OptionalParamsSelector";
import { ParamFormField } from "./ParamFormField";
import ParamUtils, { PRIORITY, ParamValue, Priority } from "./utils";

interface AppFormApi {
  form: {
    submitForm: () => Promise<void>;
  };
  getFormValues: () => Record<string, unknown>;
}

interface AppParameterFormProps {
  app: Application;
  deviceParams?: ParamValue[];
  formErrors: Record<string, unknown>;
  getFormApi: (getApi: () => AppFormApi) => void;
  network: {
    id: number;
  };
  networkParams: ParamValue[];
  priority: Priority;
  readonly?: boolean;
  schemas?: Schema[];
  systemManagedParams?: string[] | null;
}

const rowStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "16px",
  position: "relative",
};

const columnStyle: CSSProperties = {
  flex: "1 1 calc(50% - 16px)",
  maxWidth: "calc(50% - 16px)",
  display: "flex",
  flexDirection: "column",
};

function collectErrors(
  app: Application,
  formErrors: Record<string, unknown>,
  name: string,
) {
  return Object.entries(formErrors || {})
    .filter(([path]) => path === `${app.id}.${name}`)
    .reduce<string[]>((_acc, [, value]) => value as string[], []);
}

function getInitialValues({
  schemas = [],
  deviceParams = [],
  networkParams = [],
}: AppParameterFormProps) {
  return schemas.reduce<Record<string, unknown>>(
    (acc, { name, default_value: defaultValue }) => {
      acc[name] = ParamUtils.getPriorityValue(name, {
        deviceParams,
        networkParams,
        defaultValue,
      });

      return acc;
    },
    {},
  );
}

function getActiveFields({
  deviceParams = [],
  networkParams = [],
  schemas = [],
}: AppParameterFormProps) {
  const requiredFields = schemas
    .filter((schema) => schema.required)
    .map((schema) => schema.name);
  const optionalFields = schemas
    .filter((schema) => {
      const { name, default_value: defaultValue } = schema;
      const deviceParam = ParamUtils.getDeviceLevelParam(name, deviceParams);
      const networkParam = ParamUtils.getNetworkLevelParam(name, networkParams);
      const priorities = ParamUtils.buildPriorities(
        defaultValue,
        networkParam,
        deviceParam,
      );

      return priorities.length > 0;
    })
    .map((schema) => schema.name);

  return Array.from(new Set([...requiredFields, ...optionalFields])).sort();
}

const AppParameterForm = (props: AppParameterFormProps) => {
  const {
    app,
    deviceParams = [],
    formErrors,
    getFormApi,
    network,
    networkParams,
    priority,
    readonly = false,
    schemas = [],
    systemManagedParams,
  } = props;
  const [active, setActive] = useState(() => getActiveFields(props));
  const [values, setValues] = useState<Record<string, unknown>>(() =>
    getInitialValues(props),
  );
  const valuesRef = useRef(values);
  const isOverridingRef = useRef<Record<string, boolean>>({});
  valuesRef.current = values;

  const isFieldDisabled = useCallback(
    (readOnly: boolean, schema: Schema) => {
      if (readOnly) {
        return true;
      }

      if (!systemManagedParams) {
        return false;
      }

      if (!ApplicationSelectors.systemManaged(app)) {
        return false;
      }

      return systemManagedParams.includes(
        getAppParamKey(app.name, schema.name),
      );
    },
    [app, systemManagedParams],
  );

  const resetFieldValue = useCallback(
    (name: string, resetPriority = PRIORITY.MAX) => {
      const schema = schemas.find((item) => item.name === name);
      const defaultValue = schema?.default_value;
      const initialValue = ParamUtils.getPriorityValue(name, {
        deviceParams,
        networkParams,
        defaultValue,
        priority: resetPriority,
      });

      setValues((currentValues) => ({
        ...currentValues,
        [name]: initialValue,
      }));
    },
    [deviceParams, networkParams, schemas],
  );

  const getFormValues = useCallback(() => {
    const overrideOff = Object.entries(isOverridingRef.current)
      .filter(([, override]) => !override)
      .reduce((acc, [name]) => {
        acc.add(name);
        return acc;
      }, new Set<string>());

    return Object.keys(valuesRef.current)
      .filter((name) => !overrideOff.has(name))
      .filter((name) => active.includes(name))
      .reduce<Record<string, unknown>>((acc, name) => {
        acc[name] = valuesRef.current[name];
        return acc;
      }, {});
  }, [active]);

  const getApi = useCallback(
    () => ({
      form: {
        submitForm: async () => {},
      },
      getFormValues,
    }),
    [getFormValues],
  );

  useEffect(() => {
    getFormApi(getApi);
  }, [getFormApi, getApi]);

  const remove = (name: string): void => {
    const canRemove = schemas.some(
      (schema) => schema.name === name && !schema.required,
    );

    if (!canRemove) {
      return;
    }

    setActive((current) => current.filter((item) => item !== name));
    resetFieldValue(name);
  };

  const onOverride = (name: string, isOverriding: boolean): void => {
    isOverridingRef.current[name] = isOverriding;
    const resetPriority = isOverriding ? PRIORITY.MAX : priority;
    resetFieldValue(name, resetPriority);
  };

  const addOptionalParam = (schema: Schema): void => {
    setActive((current) => [...current, schema.name]);
  };

  const renderAppParam = (schema: Schema) => {
    const { name } = schema;
    const deviceParam = deviceParams.find((param) => param.name === name);
    const errors = collectErrors(app, formErrors, name);
    const isDisabled = isFieldDisabled(readonly, schema);

    return (
      <div key={`param-${name}`} style={columnStyle}>
        <ParamFormField
          deviceParam={deviceParam}
          errors={errors}
          field={{ name, value: values[name] }}
          form={{
            setFieldValue: (fieldName, value) => {
              setValues((currentValues) => ({
                ...currentValues,
                [fieldName]: value,
              }));
            },
          }}
          network={network}
          networkParams={networkParams}
          onOverride={(value: boolean) => onOverride(name, value)}
          onRemove={() => remove(name)}
          priority={priority}
          readOnly={isDisabled}
          schema={schema}
        />
      </div>
    );
  };

  const renderRequiredAppParams = () => {
    const requiredParamSchemas = schemas
      .filter((schema) => schema.required)
      .sort(sortByDisplayNameAsc);

    if (requiredParamSchemas.length === 0) {
      return null;
    }

    return (
      <div data-testid="required-params" style={rowStyle}>
        <div style={{ flex: "0 0 100%", maxWidth: "100%" }}>
          <Header title="Required Parameters" />
        </div>
        {requiredParamSchemas.map(renderAppParam)}
      </div>
    );
  };

  const renderOptionalAppParams = () => {
    const optionalParamSchemas = active
      .map((name) =>
        schemas.find((schema) => schema.name === name && !schema.required),
      )
      .filter(Boolean)
      .sort(sortByDisplayNameAsc) as Schema[];

    if (optionalParamSchemas.length === 0) {
      return null;
    }

    return (
      <div data-testid="optional-params" style={rowStyle}>
        <div style={{ flex: "0 0 100%", maxWidth: "100%" }}>
          <Header title="Optional Parameters" />
        </div>
        <div style={{ flex: "0 0 100%", maxWidth: "100%" }}>
          <OptionalParamsSelector
            active={active}
            onSelect={addOptionalParam}
            schemas={schemas}
          />
        </div>
        {optionalParamSchemas.map(renderAppParam)}
      </div>
    );
  };

  return (
    <form onSubmit={(event) => event.preventDefault()}>
      <Stack gap={2}>
        {renderRequiredAppParams()}
        {renderOptionalAppParams()}
      </Stack>
    </form>
  );
};

export { AppParameterForm, collectErrors, getActiveFields, getInitialValues };
export type { AppFormApi, AppParameterFormProps };
